export const CASH_LABELS = Object.freeze({
  ACTUAL_CASH: 'Actual cash',
  EXPECTED_RECEIPTS: 'Expected receipts',
  COMMITTED_OBLIGATIONS: 'Committed obligations',
  OPTIONAL_PAYMENTS: 'Optional payments',
  ASSUMPTION: 'Assumption',
  SCENARIO: 'Scenario',
  SOURCE_LOCAL_DEMO: 'Source-local/demo',
});

export const CASH_EVENT_TYPES = Object.freeze({
  ACTUAL_CASH: 'actual-cash',
  EXPECTED_RECEIPT: 'expected-receipt',
  COMMITTED_OBLIGATION: 'committed-obligation',
  OPTIONAL_PAYMENT: 'optional-payment',
});

export const CASH_SCENARIOS = Object.freeze([
  { id: 'pay-on-due-date', label: 'Pay-on-due-date', optionalPayments: true },
  { id: 'agreed-schedule', label: 'Agreed schedule', optionalPayments: true },
  { id: 'tight-budget', label: 'Tight-budget plan', optionalPayments: false },
]);

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const STRICT_LABEL_SET = new Set(Object.values(CASH_LABELS));

const REQUIRED_LABELS_BY_TYPE = {
  [CASH_EVENT_TYPES.ACTUAL_CASH]: [CASH_LABELS.ACTUAL_CASH],
  [CASH_EVENT_TYPES.EXPECTED_RECEIPT]: [CASH_LABELS.EXPECTED_RECEIPTS, CASH_LABELS.ASSUMPTION],
  [CASH_EVENT_TYPES.COMMITTED_OBLIGATION]: [CASH_LABELS.COMMITTED_OBLIGATIONS],
  [CASH_EVENT_TYPES.OPTIONAL_PAYMENT]: [CASH_LABELS.OPTIONAL_PAYMENTS, CASH_LABELS.SCENARIO],
};

function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function dateKey(value) {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  const raw = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    throw new Error(`Invalid cash monitor date: ${value}`);
  }
  return raw;
}

function utcMs(value) {
  const [year, month, day] = dateKey(value).split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

export function addDays(value, days) {
  return new Date(utcMs(value) + Number(days || 0) * MS_PER_DAY).toISOString().slice(0, 10);
}

export function daysBetween(startDate, endDate) {
  return Math.round((utcMs(endDate) - utcMs(startDate)) / MS_PER_DAY);
}

export function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function clampMonthDate(year, month, day) {
  const safeDay = Math.min(Math.max(1, Number(day) || 1), lastDayOfMonth(year, month));
  return `${year}-${String(month).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`;
}

export function monthKeysInRange(startDate, horizonDays) {
  const start = dateKey(startDate);
  const end = addDays(start, Math.max(1, Number(horizonDays) || 1) - 1);
  const months = [];
  let [year, month] = start.slice(0, 7).split('-').map(Number);
  const endYM = end.slice(0, 7);
  while (`${year}-${String(month).padStart(2, '0')}` <= endYM) {
    months.push({ year, month, ym: `${year}-${String(month).padStart(2, '0')}` });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

export function dateInRange(date, startDate, horizonDays) {
  const offset = daysBetween(startDate, date);
  return offset >= 0 && offset < Math.max(1, Number(horizonDays) || 1);
}

function inferType(event) {
  const raw = event?.type || event?.kind || '';
  if (Object.values(CASH_EVENT_TYPES).includes(raw)) return raw;
  const labels = new Set(event?.labels || []);
  if (labels.has(CASH_LABELS.EXPECTED_RECEIPTS)) return CASH_EVENT_TYPES.EXPECTED_RECEIPT;
  if (labels.has(CASH_LABELS.COMMITTED_OBLIGATIONS)) return CASH_EVENT_TYPES.COMMITTED_OBLIGATION;
  if (labels.has(CASH_LABELS.OPTIONAL_PAYMENTS)) return CASH_EVENT_TYPES.OPTIONAL_PAYMENT;
  if (labels.has(CASH_LABELS.ACTUAL_CASH)) return CASH_EVENT_TYPES.ACTUAL_CASH;
  return null;
}

function inferCommercialTimingType(event) {
  const labels = new Set(event?.labels || []);
  if (labels.has(CASH_LABELS.EXPECTED_RECEIPTS) || event?.timingKind === 'EXPECTED_RECEIPT') {
    return CASH_EVENT_TYPES.EXPECTED_RECEIPT;
  }
  if (labels.has(CASH_LABELS.COMMITTED_OBLIGATIONS) || event?.timingKind === 'COMMITTED_OBLIGATION') {
    return CASH_EVENT_TYPES.COMMITTED_OBLIGATION;
  }
  return null;
}

function isLedgerCommercialTimingEvent(event) {
  return Boolean(
    event?.provenanceKind === 'ledger-commercial-timing' ||
    event?.postingPosture === 'PRE_POSTING_COMMERCIAL_TIMING'
  );
}

function bucketForType(type) {
  if (type === CASH_EVENT_TYPES.EXPECTED_RECEIPT) return 'expectedReceipts';
  if (type === CASH_EVENT_TYPES.COMMITTED_OBLIGATION) return 'committedObligations';
  if (type === CASH_EVENT_TYPES.OPTIONAL_PAYMENT) return 'optionalPayments';
  if (type === CASH_EVENT_TYPES.ACTUAL_CASH) return 'actualCash';
  return 'unknown';
}

function eventAppliesToScenario(event, scenarioId) {
  const appliesTo = event?.appliesTo || event?.scenarioIds;
  if (Array.isArray(appliesTo) && appliesTo.length > 0) return appliesTo.includes(scenarioId);
  const single = event?.scenarioId || event?.scenario;
  if (single) return single === scenarioId;
  return true;
}

function normalizeLabels(labels) {
  return Array.from(new Set(Array.isArray(labels) ? labels.filter(Boolean) : []));
}

export function classifyCashEvent(event) {
  const rawType = inferType(event);
  const ledgerCommercialTiming = isLedgerCommercialTimingEvent(event);
  const type = ledgerCommercialTiming && rawType === CASH_EVENT_TYPES.ACTUAL_CASH
    ? inferCommercialTimingType(event)
    : rawType;
  const bucket = bucketForType(type);
  const labels = normalizeLabels(event?.labels).filter((label) => (
    type === CASH_EVENT_TYPES.ACTUAL_CASH || label !== CASH_LABELS.ACTUAL_CASH
  ));
  const amount = Math.abs(toFiniteNumber(event?.amount));
  return {
    ...event,
    type,
    bucket,
    labels,
    amount,
    signedAmount: bucket === 'expectedReceipts' || bucket === 'actualCash' ? amount : -amount,
  };
}

export function validateCashMonitorLabels(events) {
  const issues = [];
  for (const event of events || []) {
    const rawType = inferType(event);
    const rawLabels = normalizeLabels(event?.labels);
    const ledgerCommercialTiming = isLedgerCommercialTimingEvent(event);
    const classified = classifyCashEvent(event);
    if (!classified.type) {
      issues.push({ id: event?.id, code: 'UNKNOWN_TYPE', label: event?.type || event?.kind || null });
      continue;
    }
    for (const label of classified.labels) {
      if (!STRICT_LABEL_SET.has(label)) {
        issues.push({ id: event?.id, code: 'UNKNOWN_LABEL', label });
      }
    }
    if (rawLabels.includes(CASH_LABELS.ACTUAL_CASH) && classified.type !== CASH_EVENT_TYPES.ACTUAL_CASH) {
      issues.push({ id: event?.id, code: 'FORBIDDEN_ACTUAL_CASH_LABEL', label: CASH_LABELS.ACTUAL_CASH });
    }
    if (ledgerCommercialTiming && rawType === CASH_EVENT_TYPES.ACTUAL_CASH) {
      issues.push({ id: event?.id, code: 'LEDGER_TIMING_ACTUAL_CASH_TYPE', label: CASH_EVENT_TYPES.ACTUAL_CASH });
    }
    if (ledgerCommercialTiming && rawLabels.includes(CASH_LABELS.ACTUAL_CASH)) {
      issues.push({ id: event?.id, code: 'LEDGER_TIMING_ACTUAL_CASH_LABEL', label: CASH_LABELS.ACTUAL_CASH });
    }
    for (const required of REQUIRED_LABELS_BY_TYPE[classified.type] || []) {
      if (!classified.labels.includes(required)) {
        issues.push({ id: event?.id, code: 'MISSING_LABEL', label: required });
      }
    }
  }
  return issues;
}

export function assertCashMonitorLabels(events) {
  const issues = validateCashMonitorLabels(events);
  if (issues.length > 0) {
    throw new Error(`Cash monitor label validation failed: ${JSON.stringify(issues)}`);
  }
}

function normalizeInput(input = {}) {
  const startDate = dateKey(input.startDate || new Date());
  const horizonDays = Math.max(1, Math.round(toFiniteNumber(input.horizonDays, 45)));
  const openingAmount = toFiniteNumber(input.openingCash?.amount ?? input.openingCash);
  return {
    ...input,
    startDate,
    horizonDays,
    openingCash: {
      amount: openingAmount,
      labels: normalizeLabels(input.openingCash?.labels || [CASH_LABELS.ACTUAL_CASH]),
      sourceLabel: input.openingCash?.sourceLabel || CASH_LABELS.ACTUAL_CASH,
      sourceState: input.openingCash?.sourceState || null,
    },
    minimumCashFloor: toFiniteNumber(input.minimumCashFloor, 0),
    scenarioId: input.scenarioId || 'agreed-schedule',
    includeOptionalPayments: input.includeOptionalPayments !== false,
    events: Array.isArray(input.events) ? input.events : [],
  };
}

function eventsForDay(input, day) {
  return input.events
    .filter((event) => event?.date)
    .filter((event) => eventAppliesToScenario(event, input.scenarioId))
    .filter((event) => {
      const classified = classifyCashEvent(event);
      return input.includeOptionalPayments || classified.bucket !== 'optionalPayments';
    })
    .filter((event) => dateKey(event.date) === day)
    .map(classifyCashEvent);
}

function warningForRow(row, rows, settings) {
  if (row.closingCash < 0) {
    const hasFutureReceipt = rows.some((candidate) => (
      candidate.dayIndex > row.dayIndex && candidate.expectedReceipts > 0
    ));
    return hasFutureReceipt ? 'Cash gap before expected receipt' : 'Negative closing cash';
  }
  if (row.closingCash < settings.minimumCashFloor) return 'Below cash floor';
  const futureReceipt = rows.find((candidate) => (
    candidate.dayIndex > row.dayIndex &&
    candidate.dayIndex <= row.dayIndex + 7 &&
    candidate.expectedReceipts > 0
  ));
  if (row.committedObligations > 0 && row.expectedReceipts === 0 && futureReceipt) {
    return 'Obligation before expected receipt';
  }
  return 'Clear';
}

export function buildDailyCashRows(input) {
  const normalized = normalizeInput(input);
  const rows = [];
  let opening = normalized.openingCash.amount;

  for (let i = 0; i < normalized.horizonDays; i += 1) {
    const day = addDays(normalized.startDate, i);
    const events = eventsForDay(normalized, day);
    const expectedReceipts = events
      .filter((event) => event.bucket === 'expectedReceipts')
      .reduce((sum, event) => sum + event.amount, 0);
    const committedObligations = events
      .filter((event) => event.bucket === 'committedObligations')
      .reduce((sum, event) => sum + event.amount, 0);
    const optionalPayments = events
      .filter((event) => event.bucket === 'optionalPayments')
      .reduce((sum, event) => sum + event.amount, 0);
    const netChange = expectedReceipts - committedObligations - optionalPayments;
    const closingCash = opening + netChange;
    const eventLabels = events.flatMap((event) => event.labels || []);
    const labels = normalizeLabels([
      ...(i === 0 ? normalized.openingCash.labels : [CASH_LABELS.SCENARIO]),
      ...eventLabels,
    ]);

    rows.push({
      id: day,
      date: day,
      dayIndex: i,
      openingCash: opening,
      actualCashMarker: i === 0
        ? normalizeLabels(normalized.openingCash.labels).join(' + ')
        : CASH_LABELS.SCENARIO,
      expectedReceipts,
      committedObligations,
      optionalPayments,
      netChange,
      closingCash,
      labels,
      events,
      warningLabel: 'Clear',
    });

    opening = closingCash;
  }

  return rows.map((row) => ({
    ...row,
    warningLabel: warningForRow(row, rows, normalized),
  }));
}

function sumRows(rows, count, key) {
  return rows.slice(0, count).reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
}

export function summarizeCashWarnings(rows, settings = {}) {
  const minimumCashFloor = toFiniteNumber(settings.minimumCashFloor, 0);
  const warnings = (rows || [])
    .filter((row) => row.warningLabel && row.warningLabel !== 'Clear')
    .map((row) => ({
      date: row.date,
      label: row.warningLabel,
      closingCash: row.closingCash,
      minimumCashFloor,
    }));
  return {
    count: warnings.length,
    first: warnings[0] || null,
    negativeDays: (rows || []).filter((row) => row.closingCash < 0).length,
    belowFloorDays: (rows || []).filter((row) => row.closingCash >= 0 && row.closingCash < minimumCashFloor).length,
    warnings,
  };
}

export function buildCashMonitorPlan(input) {
  const normalized = normalizeInput(input);
  const rows = buildDailyCashRows(normalized);
  const lowest = rows.reduce((min, row) => (
    !min || row.closingCash < min.closingCash ? row : min
  ), null);
  const firstGap = rows.find((row) => row.closingCash < 0) || null;
  const last = rows[rows.length - 1] || null;
  const warnings = summarizeCashWarnings(rows, normalized);
  const runwayDays = firstGap ? firstGap.dayIndex : normalized.horizonDays;

  return {
    scenarioId: normalized.scenarioId,
    startDate: normalized.startDate,
    horizonDays: normalized.horizonDays,
    rows,
    warnings,
    labelIssues: validateCashMonitorLabels(normalized.events),
    kpis: {
      openingActualCash: normalized.openingCash.amount,
      openingLabels: normalized.openingCash.labels,
      openingSourceLabel: normalized.openingCash.sourceLabel,
      lowestProjectedCashDate: lowest?.date || null,
      lowestProjectedCashAmount: lowest?.closingCash ?? normalized.openingCash.amount,
      firstCashGapDate: firstGap?.date || null,
      firstCashGapAmount: firstGap?.closingCash ?? null,
      runwayDays,
      runwayCapped: !firstGap,
      expectedReceiptsNext14: sumRows(rows, 14, 'expectedReceipts'),
      expectedReceiptsNext30: sumRows(rows, 30, 'expectedReceipts'),
      committedObligationsNext14: sumRows(rows, 14, 'committedObligations'),
      committedObligationsNext30: sumRows(rows, 30, 'committedObligations'),
      optionalPaymentsNext30: sumRows(rows, 30, 'optionalPayments'),
      closingCash: last?.closingCash ?? normalized.openingCash.amount,
    },
  };
}

export function compareCashScenarios(input) {
  const normalized = normalizeInput(input);
  return CASH_SCENARIOS.map((scenario) => {
    const plan = buildCashMonitorPlan({
      ...normalized,
      scenarioId: scenario.id,
      includeOptionalPayments: scenario.optionalPayments && normalized.includeOptionalPayments,
    });
    return {
      id: scenario.id,
      scenarioId: scenario.id,
      label: scenario.label,
      labels: [CASH_LABELS.SCENARIO, CASH_LABELS.ASSUMPTION],
      openingActualCash: plan.kpis.openingActualCash,
      lowestProjectedCashDate: plan.kpis.lowestProjectedCashDate,
      lowestProjectedCashAmount: plan.kpis.lowestProjectedCashAmount,
      firstCashGapDate: plan.kpis.firstCashGapDate || 'None',
      runwayDays: plan.kpis.runwayCapped ? `${plan.kpis.runwayDays}+` : plan.kpis.runwayDays,
      expectedReceiptsNext30: plan.kpis.expectedReceiptsNext30,
      committedObligationsNext30: plan.kpis.committedObligationsNext30,
      optionalPaymentsNext30: plan.kpis.optionalPaymentsNext30,
      closingCash: plan.kpis.closingCash,
      warningLabel: plan.warnings.first?.label || 'Clear',
    };
  });
}
